import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import '@/assets/styles/components/meet-camera.scss';
import { useFeatureFlag } from 'configcat-react';
import { TimeSpan } from '@/utility/time-span.ts';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import LoadingSpinner from '@/components/loading-spinner';
import ClassNames from 'classnames';
import { useSizes } from '@/utility/responsive.ts';

export const MeetVWOutActions = {
	initialize: 'initialize',
	cameraStatus: 'cameraStatus',
	microphoneStatus: 'microphoneStatus',
	join: 'join',
	leave: 'leave',
	bgEffect: 'bgEffect',
	openedTools: 'openedTools',
	changeViewType: 'changeViewType',
	switchScreenShare: 'switchScreenShare',
	switchOffScreenShare: 'switchOffScreenShare',
	endDateUpdate: 'endDateUpdate',
	dataChannelSend: 'dataChannelSend',
	updateServerTime: 'updateServerTime',
	onAudioInputSourceChange: 'onAudioInputSourceChange',
	onCameraSourceChange: 'onCameraSourceChange',
	toggleCameraMobile: 'toggleCameraMobile',
	onOpponentConnected: 'onOpponentConnected'
} as const;

type MeetVWOutActionsType = (typeof MeetVWOutActions)[keyof typeof MeetVWOutActions];

export const MeetVWInActions = {
	initialized: 'initialized',
	overlayClicked: 'overlayClicked',
	cancelScreenShare: 'cancelScreenShare',
	onEndTime: 'onEndTime',
	onExtensionNotification: 'onExtensionNotification',
	onMeetingLastConnectionNotification: 'onMeetingLastConnectionNotification',
	dataChannelReceived: 'dataChannelReceived',
	onDeviceList: 'onDeviceList',
	onMicrophoneSourceChange: 'onMicrophoneSourceChange'
} as const;

type Props = {
	streamId: string | null;
	roomId: string | null;
	token: string | null;
	socketURL: string | null;
	providerName?: string | null;
	endDate: Date | null;
	onEndTime: () => void;
	extensionNotificationDate: string | null;
	meetingLastConnectionDate: string | null;
	onExtensionNotification?: () => void;
	onMeetingLastConnectionNotification?: () => void;
	userInfos?: {
		studentName: string;
		studentAvatar: string;
		coachName: string;
		coachAvatar: string;
		coachSubject: string;
	} | null;
	onInitialized?: () => void;
	onOverlayClicked?: () => void;
	onCancelScreenShare?: () => void;
	onDeviceList?: (devices: { cameras: Array<MediaDeviceInfo>; microphones: Array<MediaDeviceInfo> }) => void;
	onMicrophoneSourceChange: (deviceId: string) => void;
	isPermissionLoading: boolean;
	isToaster: boolean;
	activeMicrophone: boolean;
	activeVideoCamera: boolean;
	isConnectedStudent?: boolean;
};

export type MeetCameraRefType = {
	onMicrophoneStatus: (status: boolean) => void;
	onCameraStatus: (status: boolean) => void;
	onViewTypeChange: (type: number) => void;
	onJoin: () => void;
	onLeave: () => void;
	onBgEffect: () => void;
	onSwitchScreenShare: () => void;
	onSwitchOffScreenShare: () => void;
	onOpenedTools: (status: boolean) => void;
	onEndDateUpdate: (onEndDateUpdate: Date) => void;
	onDataChannelSend: (data: any) => void;
	onServerTimeUpdate: (serverTimeData: any) => void;
	onToggleCameraMobile: () => void;
	onOpponentConnected: () => void;
};

const MeetWv = forwardRef<MeetCameraRefType, Props>(
	(
		{
			streamId,
			roomId,
			token,
			socketURL,
			providerName,
			endDate,
			userInfos,
			onOverlayClicked = () => null,
			onCancelScreenShare = () => null,
			onEndTime = () => null,
			extensionNotificationDate,
			meetingLastConnectionDate,
			onExtensionNotification = () => null,
			onMeetingLastConnectionNotification = () => null,
			onDeviceList = () => null,
			onMicrophoneSourceChange = () => null,
			isToaster,
			isPermissionLoading,
			activeMicrophone,
			onInitialized = () => null,
			activeVideoCamera,
			isConnectedStudent = false
		},
		ref
	) => {
		const [url, setUrl] = useState<string>('');
		const [isLoading, setIsLoading] = useState<boolean>(true);
		const loadEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
		const iframeRef = useRef<HTMLIFrameElement | null>(null);
		const currentEndDateRef = useRef(null);

		const { isMobile } = useSizes();

		useEffect(() => {
			if (endDate) {
				currentEndDateRef.current = endDate;
			}
		}, [endDate]);

		const dopingTechComponentsCacheStrRef = useRef<string>('current');
		const { value: dopingTechComponentsCacheStr } = useFeatureFlag(CONFIGCAT_KEYS.DOPING_TECH_COMPONENTS_CACHE_STR, '01112024');

		const onMessage = (event: MessageEvent): void => {
			const data = event?.data;
			if (data?.type === MeetVWInActions.initialized) {
				sendMessage({
					type: MeetVWOutActions.join,
					data: {
						endDate: currentEndDateRef?.current,
						extensionNotificationDate,
						meetingLastConnectionDate
					}
				});

				onInitialized?.();
				setIsLoading(false);
			} else if (data?.type === MeetVWInActions.overlayClicked) {
				onOverlayClicked();
			} else if (data?.type === MeetVWInActions.cancelScreenShare) {
				onCancelScreenShare();
			} else if (data?.type === MeetVWInActions.onEndTime) {
				onEndTime();
			} else if (data?.type === MeetVWInActions.onExtensionNotification) {
				onExtensionNotification();
			} else if (data?.type === MeetVWInActions.onMeetingLastConnectionNotification) {
				onMeetingLastConnectionNotification();
			} else if (data?.type === MeetVWInActions.onDeviceList) {
				onDeviceList(
					data?.data?.devices || {
						cameras: [],
						microphones: []
					}
				);
			} else if (data?.type === MeetVWInActions.onMicrophoneSourceChange) {
				onMicrophoneSourceChange(data?.data?.deviceId);
			}
		};

		const sendMessage = (payload: { type: MeetVWOutActionsType; data?: any }): void => {
			if (iframeRef.current?.contentWindow) {
				iframeRef.current.contentWindow.postMessage(payload, '*');
				(window as any).iframeWindow = iframeRef.current.contentWindow;
			}
		};

		const onLoadEnd = () => {
			if (loadEndTimeoutRef.current) clearTimeout(loadEndTimeoutRef.current);

			loadEndTimeoutRef.current = setTimeout(() => {
				sendMessage({
					type: MeetVWOutActions.initialize,
					data: {
						streamId,
						roomId,
						socketURL,
						token,
						isStudent: false,
						isOpponentConnected: isConnectedStudent,
						startCamOff: !activeVideoCamera,
						startMicMuted: !activeMicrophone,
						isMobile: isMobile,
						...userInfos
					}
				});
			}, TimeSpan.milliSecond.sixHundred);
		};

		const onInit = (): void => {
			if (iframeRef.current) {
				iframeRef.current.onload = () => onLoadEnd();
			}
			setUrl('');
			setTimeout(() => {
				const randomHash = Array.from(Array(4).keys(), () => Math.random().toString(32).slice(2)).join('-');
				if (providerName === 'JANUS') {
					setUrl(`${process.env.DOPI_TECH_COMPONENTS_URL}?v=${dopingTechComponentsCacheStrRef.current}${randomHash}/#/janus-provider`);
				} else if (providerName === 'IVS') {
					setUrl(`${process.env.DOPI_TECH_COMPONENTS_URL}?v=${dopingTechComponentsCacheStrRef.current}${randomHash}/#/ivs-provider`);
				} else {
					setUrl(`${process.env.DOPI_TECH_COMPONENTS_URL}?v=${dopingTechComponentsCacheStrRef.current}${randomHash}/#/ant-media-conference-two-person`);
				}
			}, TimeSpan.milliSecond.fifty);
		};

		useImperativeHandle(ref, () => ({
			onMicrophoneStatus: (status: boolean): void => {
				sendMessage({
					type: MeetVWOutActions.microphoneStatus,
					data: { status }
				});
			},
			onCameraStatus: (status: boolean): void => {
				sendMessage({ type: MeetVWOutActions.cameraStatus, data: { status } });
			},
			onViewTypeChange: (type: number): void => {
				sendMessage({ type: MeetVWOutActions.changeViewType, data: { type } });
			},
			onJoin: (): void => {
				sendMessage({ type: MeetVWOutActions.join });
			},
			onLeave: (): void => {
				sendMessage({ type: MeetVWOutActions.leave });
			},
			onBgEffect: (): void => {
				sendMessage({ type: MeetVWOutActions.bgEffect });
			},
			onOpenedTools: (status: boolean): void => {
				sendMessage({ type: MeetVWOutActions.openedTools, data: { status } });
			},
			onSwitchScreenShare: (): void => {
				sendMessage({
					type: MeetVWOutActions.switchScreenShare
				});
			},
			onSwitchOffScreenShare: (): void => {
				sendMessage({
					type: MeetVWOutActions.switchOffScreenShare
				});
			},
			onEndDateUpdate: (updatedEndDate: Date) => {
				sendMessage({
					type: MeetVWOutActions.endDateUpdate,
					data: {
						endDate: updatedEndDate
					}
				});
			},
			onServerTimeUpdate: serverTime => {
				sendMessage({
					type: MeetVWOutActions.updateServerTime,
					data: {
						serverTime
					}
				});
			},
			onDataChannelSend: message => {
				sendMessage({
					type: MeetVWOutActions.dataChannelSend,
					data: {
						message: message
					}
				});
			},
			onCameraSourceChange: deviceId => {
				sendMessage({
					type: MeetVWOutActions.onCameraSourceChange,
					data: {
						deviceId
					}
				});
			},
			onAudioInputSourceChange: deviceId => {
				sendMessage({
					type: MeetVWOutActions.onAudioInputSourceChange,
					data: {
						deviceId
					}
				});
			},
			onToggleCameraMobile: (): void => {
				sendMessage({
					type: MeetVWOutActions.toggleCameraMobile
				});
			},
			onOpponentConnected: (): void => {
				sendMessage({
					type: MeetVWOutActions.onOpponentConnected
				});
			}
		}));

		useEffect(() => {
			iframeRef.current = document.getElementById('meet-iframe') as HTMLIFrameElement;
			if (streamId) {
				onInit();
				window.addEventListener('message', onMessage);
			}
			return () => {
				setUrl('');
				window.removeEventListener('message', onMessage);
			};
		}, [streamId]);

		useEffect(() => {
			if (dopingTechComponentsCacheStr) {
				dopingTechComponentsCacheStrRef.current = dopingTechComponentsCacheStr;
			}
		}, [dopingTechComponentsCacheStr]);

		return (
			<div className="meet-wv-container">
				<iframe
					id="meet-iframe"
					className={ClassNames('meet-iframe', {
						toaster: isToaster,
						active: !isLoading,
						'mobile-meet-iframe': isMobile
					})}
					src={url}
					scrolling={'no'}
					allow="autoplay; camera; microphone; display-capture"
					allowFullScreen
				/>
				{(isLoading || isPermissionLoading) && <LoadingSpinner loadingText={'Yükleniyor'} />}
			</div>
		);
	}
);

export default MeetWv;
